import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../Utils/baseQueryWithReauth";
import { FeatureRequestProps, FeatureResponseProps } from "../components/ProductView/Common/ProductView.interfaces";
import { IPaginatedEvidenceRequest, IPaginatedFeatureRequestResponse } from "../interfaces/CloseTheLoop.interfaces";

// Define a service using a base URL and expected endpoints
export const exploreProductsAPI = createApi({
  reducerPath: "exploreProducts",
  tagTypes: ["FavoriteProducts"],
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getProducts: builder.query({
      query: () => "product/getAllProductsNames"
    }),
    getProductViewMetrics: builder.mutation({
      query(body) {
        return {
          url: "product/getProductViewMetrics",
          method: "POST",
          body
        };
      }
    }),
    getProductMetricsCardsGraphs: builder.mutation<
      {
        cardDataValues: any[];
        metricsData: any[];
        viewConfig?: {
          showApplicationDropdown?: boolean;
          showCloudDropdown?: boolean;
          showDailyAggregation?: boolean;
          showWeeklyAggregation?: boolean;
        };
      },
      any
    >({
      query(body) {
        return {
          url: "product/getProductMetricsCardsGraphs",
          method: "POST",
          body
        };
      }
    }),
    getProductFeatureRequestData: builder.query<
      IPaginatedFeatureRequestResponse,
      { productName: string } & Partial<IPaginatedEvidenceRequest>
    >({
      query: ({ productName, page = 1, pageSize = 1000000, sortBy = "lastChangedDate", sortDirection = "desc" }) => {
        const searchParams = new URLSearchParams();
        searchParams.append("productName", productName);
        searchParams.append("page", page.toString());
        searchParams.append("pageSize", pageSize.toString());
        if (sortBy) searchParams.append("sortBy", sortBy);
        if (sortDirection) searchParams.append("sortDirection", sortDirection);
        return `product/getProductFeatureRequestData?${searchParams.toString()}`;
      }
    }),
    getCOSOCDataByProductId: builder.query({
      query: (id) => `product/getCOSOCData?productId=${id}`
    }),
    getProductExploreData: builder.query({
      query: () => "product/getProductExploreData"
    }),
    getFeatureProductDataDetails: builder.mutation<FeatureResponseProps, FeatureRequestProps>({
      query(body) {
        return {
          url: "product/getProductViewFeatureData",
          method: "POST",
          body
        };
      }
    }),
    getFavoriteProductsByLogin: builder.query({
      query: () => "product/getFavoriteProductsByLogin",
      providesTags: ["FavoriteProducts"]
    }),
    insertOrUpdateFavoriteProducts: builder.mutation({
      query(product) {
        return {
          url: "product/insertOrUpdateFavoriteProducts",
          method: "POST",
          body: product
        };
      },
      invalidatesTags: ["FavoriteProducts"]
    }),
    // This mutation inserts a recently viewed product record.
    // Takes an object with LoginName, LoginEmail, ProductId, and ProductName as input.
    // Sends a POST request to "product/insertRecentlyViewedProduct" with the input data.
    // Returns a boolean to indicate success or failure.
    insertRecentlyViewedProduct: builder.mutation<
      boolean,
      {
        LoginName: string;
        LoginEmail: string;
        ProductId: string;
        ProductName: string;
      }
    >({
      query(body) {
        return {
          url: "product/insertRecentlyViewedProduct",
          method: "POST",
          body
        };
      }
    }),
    getCRMCustomerProductInfoByProductName: builder.query({
      query: (productName) =>
        `product/getcrmcustomerproductinfoforproduct?productName=${encodeURIComponent(productName)}`
    }),
    getSupportDataSummaryByProductId: builder.mutation<any, any>({
      query(body) {
        return {
          url: "product/getSupportDataSummaryByProductId",
          method: "POST",
          body
        };
      }
    }),
    getExploreSupportData: builder.query({
      query: () => "support/getExploreSupportData"
    }),
    getSupportReportsList: builder.query({
      query: () => "support/getSupportReportsList"
    }),
    getMCSResources: builder.query({
      query: () => "mcs/getResources"
    }),
    getTrainingCourses: builder.query({
      query: () => "mcs/getTrainingCourses"
    }),
    getCopilotAgentDirectLineToken: builder.query<{ token: string; conversationId: string }, void>({
      query: () => "copilotagent/directline-token"
    }),
    getTopTenantsOfCustomer: builder.mutation({
      query(body) {
        return {
          url: "product/getTopTenantsOfCustomer",
          method: "POST",
          body
        };
      }
    }),
    getTopCustomersProfileByProduct: builder.mutation({
      query(body) {
        return {
          url: "product/getTopCustomersProfileByProduct",
          method: "POST",
          body
        };
      }
    }),
    getProductACRDetails: builder.query({
      query: (id) => ({
        url: `product/getProductACRDetails?productId=${id}`,
        headers: {
          ktk: sessionStorage.getItem("kqlToken") ?? undefined
        }
      })
    }),
    getPartnerInfluenceDetails: builder.mutation({
      query(body) {
        return {
          url: "product/getPartnerInfluenceDetails",
          method: "POST",
          body
        };
      }
    })
  })
});

export const PRODUCT_METRICS_CARDS_GRAPHS_CACHE_KEY = "getProductMetricsCardsGraphs";
export const PRODUCT_VIEW_METRICS_CACHE_KEY = "getProductViewMetrics";

// Export hooks for usage in functional components, which are
// Auto-generated based on the defined endpoints
export const {
  useGetProductsQuery,
  useGetProductViewMetricsMutation,
  useGetProductMetricsCardsGraphsMutation,
  useGetProductFeatureRequestDataQuery,
  useGetCOSOCDataByProductIdQuery,
  useGetProductExploreDataQuery,
  useGetFeatureProductDataDetailsMutation,
  useGetFavoriteProductsByLoginQuery,
  useInsertOrUpdateFavoriteProductsMutation,
  useInsertRecentlyViewedProductMutation,
  useGetCRMCustomerProductInfoByProductNameQuery,
  useGetSupportDataSummaryByProductIdMutation,
  useGetExploreSupportDataQuery,
  useGetSupportReportsListQuery,
  useGetMCSResourcesQuery,
  useGetTrainingCoursesQuery,
  useLazyGetCopilotAgentDirectLineTokenQuery,
  useGetTopTenantsOfCustomerMutation,
  useGetTopCustomersProfileByProductMutation,
  useGetProductACRDetailsQuery,
  useGetPartnerInfluenceDetailsMutation
} = exploreProductsAPI;
export const getProductMetricsCardsGraphsSelector = exploreProductsAPI.endpoints.getProductMetricsCardsGraphs.select(
  PRODUCT_METRICS_CARDS_GRAPHS_CACHE_KEY
);
export const getFeatureDataSelector = exploreProductsAPI.endpoints.getFeatureProductDataDetails.select("getFeatures");
export const getSupportSelector =
  exploreProductsAPI.endpoints.getSupportDataSummaryByProductId.select("getSupportData");
export const topTenantsResult = exploreProductsAPI.endpoints.getTopTenantsOfCustomer.select("getTopTenants");
